<template>
  <WdsModal :show="show" data-test="custom-field-create-modal" @cancel="$emit('cancel')">
    <template #header>
      <CustomFieldModalHeader :applies-to="appliesTo" />
    </template>
    <div v-show="errorBanner">{{ errorBanner }}</div>
    <CustomFieldContent
      v-model:field="field"
      :loading="creationInProgress"
      :validation-errors="validationErrors"
      :applies-to="appliesTo"
      @field-type-changed="resetErrors"
    ></CustomFieldContent>
    <template #footer>
      <div class="tw-flex tw-justify-between">
        <WdsButton secondary @click="cancel">
          {{ $t('Cancel') }}
        </WdsButton>

        <WdsButton
          primary
          :disabled="creationInProgress"
          data-test="save-custom-field"
          @click="create"
        >
          {{ $t('Save') }}
        </WdsButton>
      </div>
    </template>
  </WdsModal>
</template>

<script setup lang="ts">
import { ref, Ref } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { WdsModal, WdsButton } from '@worksome/wds-components'
import { isApolloError } from '@apollo/client/core'
import { $t } from '@/composables/translation'
import CustomFieldContent from '@/components/Fields/CustomFieldContent.vue'
import {
  newInstanceOfCustomField,
  extractFieldsGqlVariables,
} from '@/components/Fields/utils/CustomFieldUtils'
import { CreateCustomFieldMutation } from '@/components/Fields/graphql/mutations'
import { FormValidation } from '@/utils/formValidation'
import enums from '@/utils/enum'
import { CustomField } from '@/components/Fields/dataObjects/CustomField'
import { NetworkError } from '@/utils/notification'
import { CustomFieldVisibility, TypeSupportingCustomFieldValues } from '@/gql/graphql'
import CustomFieldModalHeader from '@/components/Fields/CustomFieldModalHeader.vue'

interface Props {
  show: boolean
  accountId: string
  appliesTo: TypeSupportingCustomFieldValues
  visibility: CustomFieldVisibility
}
const props = defineProps<Props>()

const emit = defineEmits(['cancel', 'success'])
const field: Ref<CustomField> = ref(
  newInstanceOfCustomField({ appliesTo: props.appliesTo, visibility: props.visibility }),
)
const validationErrors = ref({})

const errorBanner = ref(null)

const { mutate: createCustomField, loading: creationInProgress } = useMutation(
  CreateCustomFieldMutation,
  { clientId: 'publicApiCustomFields' },
)

function resetErrors() {
  errorBanner.value = null
  validationErrors.value = []
}

function reset() {
  resetErrors()
  field.value = newInstanceOfCustomField({
    appliesTo: props.appliesTo,
    visibility: props.visibility,
  })
}

async function create() {
  try {
    await createCustomField({
      input: {
        account: props.accountId,
        appliesTo: props.appliesTo,
        ...extractFieldsGqlVariables(field),
      },
    })

    reset()
    emit('success')
  } catch (exception: any) {
    if (
      isApolloError(exception) &&
      FormValidation.containsFormValidations(exception.graphQLErrors)
    ) {
      validationErrors.value = FormValidation.fromGraphqlErrors(exception.graphQLErrors) ?? {}
    } else {
      new NetworkError({
        msg: $t(`An error occurred when trying to create a custom field`),
        severity: enums.notification.severity.CRITICAL,
        ex: exception,
      }).dispatch()
    }
  }
}

function cancel() {
  reset()
  emit('cancel')
}
</script>
